import i18nService from "@/core/composables/i18n.service";
import { kebabCase } from "@/core/utils/utils";
import useTranslate from "@/core/composables/translate";
import { FR_LANGUAGE } from "@/core/composables/vue-i18n";

const { translate, localizeDate } = useTranslate();

const getFormattedLongList = (itemsList) => {
  if (!itemsList) {
    return [];
  }

  const maxNbItems = 30;

  let message = "";

  if (itemsList?.length) {
    if (itemsList.length < maxNbItems) {
      message = `${itemsList.join(", ")}`;
    } else {
      const firstItems = itemsList.slice(0, maxNbItems).join(", ");

      const nbOthers = itemsList.length - maxNbItems;

      const plural = nbOthers > 1 ? "s" : "";

      message = translate("SHARED.LONG_ITEMS_LIST", {
        firstItems: firstItems,
        nbOthers: nbOthers,
        plural: plural,
      });
    }
  }

  return message;
};

const objectMap = (obj, fn) => Object.fromEntries(Object.entries(obj).map(([k, v], i) => [k, fn(v, k, i)]));

const formatDateTime = (dateString) => {
  if (dateString) {
    const date = new Date(dateString);
    if (isFinite(date)) {
      return `${localizeDate(date, "date")} - ${localizeDate(date, "hour").replace(":", "h")}`;
    }

    return dateString;
  }

  return "No date input";
};

const formatApiDate = (date) => {
  if (!date) {
    return "-";
  }

  const apiDate = new Date(date);

  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };

  const formattedDate = apiDate
    .toLocaleDateString(i18nService.getActiveLanguage(), options)
    .replace(/,/g, "")
    .replace(" ", " - ");

  return i18nService.getActiveLanguage() === FR_LANGUAGE ? formattedDate.replace(":", "h") : formattedDate;
};

const formatDate = (dateString) => {
  if (dateString) {
    const date = new Date(dateString);
    return `${localizeDate(date, "date")}`;
  } else {
    return "No date input";
  }
};

const hexToRgb = (hex) => {
  if (!hex) {
    return;
  }

  return hex
    .replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => "#" + r + r + g + g + b + b)
    .substring(1)
    .match(/.{2}/g)
    .map((x) => parseInt(x, 16))
    .join(", ");
};

const formatQueryParams = (queryString) => {
  let queryParams = "";

  kebabCase(queryString)
    .split("-")
    .map((word) => {
      if (word.trim()) {
        const newParam = queryParams ? `+${encodeURIComponent(word)}` : encodeURIComponent(word);
        queryParams += newParam;
      }
    });

  return queryParams;
};

const abbreviateUserName = (fullName) => {
  if (!fullName) {
    return "-";
  }

  const words = fullName.split(" ");

  if (words.length < 2) {
    return fullName;
  }

  const abbreviatedFirstName = words[0][0].toUpperCase() + ".";
  const lastName = words[words.length - 1];
  return `${abbreviatedFirstName} ${lastName}`;
};

export default {
  abbreviateUserName,
  formatApiDate,
  formatDate,
  formatDateTime,
  formatQueryParams,
  getFormattedLongList,
  hexToRgb,
  objectMap,
};
